import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import { useDispatch } from 'react-redux';
import { Box, Button, DrawerBody, DrawerFooter, HStack, Icon, IconButton, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../../../../../../../constants/cypressIds';
import { createErrorToast } from '../../../../../../../../../../redux/modules/app';
import { hideTray, showTray } from '../../../../../../../../../../redux/modules/tray';
import {
  useCompanyQuestionCategories, useCreateQuestion,
  useCreateQuestionCategory
} from '../../../../../../../../../../services/company-service';
import { getInfiniteListData } from '../../../../../../../../../../services/util';
import RequirementTemplateBlueprintType from '../../../../../../../../../../types/RequirementTemplateBlueprintType';
import FormInput from '../../../../../../../../../form/FormInput';
import InputLabel from '../../../../../../../../../form/InputLabel';
import { InfiniteDropdownMenu } from '../../../../../../../../../library';
import QuestionLinkTray from '../QuestionLinkTray';

const CreateCustomQuestionTray = ({ companyId, blueprint, currentRequirementTemplateBlocks }) => {
  const dispatch = useDispatch();
  const [questionCategoryId, setQuestionCategoryId] = useState();
  const [showCreateCategory, setShowCreateCategory] = useState(false);

  const {
    data: infQuestionCategories,
    isLoading,
    hasNextPage,
    fetchNextPage
  } = useCompanyQuestionCategories({ companyId, withDeleted: false });
  const { mutateAsync: createQuestionCategory } = useCreateQuestionCategory({ companyId });
  const { mutateAsync: createQuestion } = useCreateQuestion({ companyId });

  const useFormMethods = useForm();
  const { handleSubmit } = useFormMethods;

  const questionCategories = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return getInfiniteListData(infQuestionCategories);
  }, [infQuestionCategories, isLoading]);

  const onSubmit = async data => {
    if ((showCreateCategory && !data?.newCategoryName) || (!showCreateCategory && !questionCategoryId)) {
      dispatch(createErrorToast({ title: 'Please provide a new question category name or select an existing category' }));

      return;
    }

    let questionCategoryIdToUse = questionCategoryId;

    if (showCreateCategory && data?.newCategoryName) {
      const response = await createQuestionCategory({ name: data.newCategoryName });

      if (!response?.id) {
        dispatch(createErrorToast({ title: 'There was an issue creating the question category.  Please try agian.' }));
      }

      questionCategoryIdToUse = response.id;
    }

    const newQuestion = await createQuestion({
      questionCategoryId: questionCategoryIdToUse,
      name: data.question_name,
      question: data.question_question,
      redFlag: data.question_red_flag
    });

    const eagerQuestion = {
      ...newQuestion,
      requirementTemplateQuestions: []
    };

    dispatch(hideTray());

    dispatch(showTray(
      () => (
        <QuestionLinkTray
          blueprint={blueprint}
          currentRequirementTemplateBlocks={currentRequirementTemplateBlocks}
          question={eagerQuestion}
        />
      )
    ));
  };

  return (
    <Box m={6} overflowY="auto">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerBody px={0}>
          <Box mb="26px">
            <InputLabel
              required
              labelName="Question Category"
            />
            <HStack>
              <InfiniteDropdownMenu
                cypressId={CypressIds.customQuestionCategoryDropdown}
                data={questionCategories}
                fetchMoreData={fetchNextPage}
                idKey="question-category-dropdown"
                isDisabled={showCreateCategory}
                labelKey="name"
                pageInfo={hasNextPage ? { currentPage: 1, lastPage: 2 } : { currentPage: 1, lastPage: 1 }}
                scrollClassName="question-category-dropdown"
                variant="primary"
                onSelect={category => setQuestionCategoryId(category.id)}
              />
              <Box>
                <Tooltip
                  hasArrow
                  borderRadius="md"
                  label={`${showCreateCategory ? 'Cancel' : 'Create new category'}`}
                  placement="bottom"
                >
                  <IconButton
                    aria-label="create-new-question-category-button"
                    data-cy={CypressIds.customQuestionCreateCategoryButton}
                    icon={<Icon as={showCreateCategory ? FaX : FaPlus} width={9} />}
                    variant="gradientAi"
                    onClick={() => setShowCreateCategory(!showCreateCategory)}
                  />
                </Tooltip>
              </Box>
            </HStack>
          </Box>

          {showCreateCategory && (
            <Box my={4}>
              <FormInput
                cypressId={CypressIds.customQuestionNewCategoryNameInput}
                inputName="newCategoryName"
                isDisabled={!showCreateCategory}
                labelName="Question Category Name"
                placeholder="Type Your New Category Name Here"
                useFormMethods={useFormMethods}
                validationRules={{ required: { value: true, message: 'New Question Category Name is required' } }}
              />
            </Box>
          )}

          <FormInput
            required
            cypressId={CypressIds.customQuestionNameInput}
            inputName="question_name"
            labelName="Name"
            useFormMethods={useFormMethods}
          />

          <FormInput
            required
            cypressId={CypressIds.customQuestionDescriptionInput}
            inputName="question_question"
            labelName="Question"
            type="textArea"
            useFormMethods={useFormMethods}
          />

          <FormInput
            cypressId={CypressIds.customQuestionRedFlagSwitch}
            inputName="question_red_flag"
            labelName="Red Flag Question"
            mb={8}
            toolTip="When enabled, if the answer to this question is 'yes', the answer will show a red flag instead of a green check mark.
            Likewise, if the answer is 'no', the answer will show a green check mark.
            Use this when you want PINS AI Assistant to find something that would cause the requirement to be non-compliant."
            type="switch"
            useFormMethods={useFormMethods}
          />
        </DrawerBody>

        <DrawerFooter px={0}>
          <Button
            data-cy={CypressIds.saveQuestionRequirementButton}
            h="48px"
            type="submit"
            variant="gradientAi"
            w="100%"
          >
            Save
          </Button>
        </DrawerFooter>
      </form>
    </Box>
  );
};

CreateCustomQuestionTray.propTypes = {
  companyId: PropTypes.number.isRequired,
  blueprint: RequirementTemplateBlueprintType.isRequired,
  currentRequirementTemplateBlocks: PropTypes.array.isRequired
};

export default CreateCustomQuestionTray;
